import DropdownTreeSelect from "react-dropdown-tree-select";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { IconChevronDown } from "@pentabd/icons";
import { Select, Text } from "@pentabd/ui";

import {
  InputDateControllerRow,
  InputTextRow,
  SelectControllerRow,
} from "@components";
import { SelectController } from "@components/SelectController";

import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";
import { useUser } from "@hooks/useUser";

import {
  getConvertedPositionData,
  getConvertedPositionDataWithPostType,
  getConvertedUnitData,
  getConvertedUnitPositionData,
} from "@helpers/baseDataConvert";
// import { getConvertedMasterData } from "@helpers/masterDataConvert";

import { BASE_DATA } from "@constants/base-data";
// import { MASTER_DATA } from "@constants/master-data";

import { PositionType } from "@type/HRM/position";

import { getFilteredGradeData } from "@helpers/convertedGradeAndUnitPositionData";
import { getPositionColor } from "@helpers/getPositionColor";
import { unitTreeDataConvert } from "@helpers/unitTreeDataConvert";

import { getAccess } from "@helpers/permissionController";

import {
  GENERAL_INFO,
  GeneralInfoFormDataType,
} from "@validations/HRM/employee/generalInfoValidation";
import { useEffect } from "react";

const { WINGS, POSITIONS, UNIT_TREE, POST_TYPE } = BASE_DATA;
// const { EMPLOYMENT_TYPE } = MASTER_DATA;

const JobInfo = ({ isProfileDisabled }: { isProfileDisabled: boolean }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  // Those data are for some dropdowns or radio buttons options.
  const { data: positions, handleQuery } = useBaseData(POSITIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: unitTree } = useBaseData(UNIT_TREE);
  // const { data: employmentType } = useMasterData(EMPLOYMENT_TYPE);
  const { data: postType } = useMasterData(POST_TYPE);

  const {
    register,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<GeneralInfoFormDataType>();

  let wing = watch(GENERAL_INFO.WING_DESK_IO_E0) as string;
  let designation = watch(GENERAL_INFO.DESIGNATION) as string;

  useEffect(() => {
    handleQuery("postTypeId", 113);
  }, []);

  return (
    <div className="d-flex flex-column gap-8 mb-8 pb-8 border-bottom">
      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.EMPLOYEE_ID"
        register={{ ...register(GENERAL_INFO.EMPLOYEE_ID) }}
        error={errors}
        required
        disabled={isProfileDisabled}
      />

      {/* <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.EMPLOYMENT_TYPE"
        control={control}
        name={GENERAL_INFO.EMPLOYEE_TYPE}
        register={{ ...register(GENERAL_INFO.EMPLOYEE_TYPE) }}
        options={getConvertedMasterData(employmentType, language)}
        setValue={setValue}
      /> */}

      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.BATCH"
        register={{ ...register(GENERAL_INFO.BATCH) }}
        error={errors}
        restriction
        disabled={isProfileDisabled}
      />

      {getAccess(roles, "EMPLOYEE_UPDATE_PAGE") &&
        getAccess(roles, "EMPLOYEE_EDIT_PAGE") && (
          <div className="row">
            <div className="col-md-12 col-lg-3" />
            <div className="col-md-12 col-lg-7">
              <div className="d-flex flex-wrap gap-4">
                <Controller
                  control={control}
                  name={GENERAL_INFO.WING_DESK_IO_E0}
                  render={({ field }) => {
                    return (
                      <DropdownTreeSelect
                        data={unitTreeDataConvert(
                          unitTree[0]?.children || [],
                          language,
                        )}
                        onChange={(e) =>
                          setValue(GENERAL_INFO.WING_DESK_IO_E0, e.id as number)
                        }
                        mode="radioSelect"
                        texts={{
                          placeholder: t(
                            "EMPLOYEE.GENERAL_INFO.FORM.WING.PLACEHOLDER",
                          ),
                        }}
                        disabled={isProfileDisabled}
                        // disabled
                      />
                    );
                  }}
                />
                {wing && (
                  <SelectController
                    control={control}
                    name={GENERAL_INFO.WING_DESK_IO_E0}
                    register={{ ...register(GENERAL_INFO.WING_DESK_IO_E0) }}
                    options={getConvertedUnitData(wings, language)}
                    setValue={setValue}
                    disabled
                  />
                )}

                {wing && (
                  <SelectController
                    control={control}
                    name={GENERAL_INFO.DESIGNATION}
                    register={{ ...register(GENERAL_INFO.DESIGNATION) }}
                    options={getConvertedPositionDataWithPostType(
                      positions
                        .filter(
                          (position: PositionType) =>
                            position.unit.id === Number(wing),
                        )
                        .map((position: PositionType) => ({
                          position: position.position,
                          postType: position.postType,
                        })),
                      language,
                    ).map((position: any) => ({
                      ...position,
                      customComponent: (
                        <Text
                          color={getPositionColor(
                            postType as any,
                            position as any,
                          )}
                        >
                          {position.label}
                        </Text>
                      ),
                    }))}
                    setValue={setValue}
                    placeholder={t(
                      "EMPLOYEE.GENERAL_INFO.FORM.DESIGNATION.PLACEHOLDER",
                    )}
                    isSearchable
                    // disabled
                    disabled={isProfileDisabled}
                  />
                )}

                {wing && designation && (
                  <>
                    <Controller
                      control={control}
                      name={GENERAL_INFO.GRADE}
                      render={({ field }) => {
                        return (
                          <Select
                            {...register(GENERAL_INFO.GRADE)}
                            options={getFilteredGradeData(
                              positions,
                              wing,
                              designation,
                              language,
                            )}
                            defaultValue={field.value as number}
                            onSelectItem={(e) => {
                              setValue(GENERAL_INFO.GRADE, e as number);

                              const employmentType = positions
                                .filter(
                                  (position: PositionType) =>
                                    position.unit.id === Number(wing) &&
                                    position.position.id ===
                                      Number(designation),
                                )
                                .map((position: PositionType) => {
                                  return {
                                    ...position.position.gradeDetails,
                                  };
                                })[0].isGazetted;
                              setValue(
                                GENERAL_INFO.EMPLOYMENT_TYPE,
                                employmentType,
                              );
                            }}
                            suffix={<IconChevronDown size="16" />}
                            placeholder={t(
                              "EMPLOYEE.GENERAL_INFO.FORM.GRADE.PLACEHOLDER",
                            )}
                            // disabled
                            disabled={isProfileDisabled}
                          />
                        );
                      }}
                    />
                    <SelectController
                      control={control}
                      name={GENERAL_INFO.POSITION}
                      register={{ ...register(GENERAL_INFO.POSITION) }}
                      options={getConvertedUnitPositionData(
                        positions.filter(
                          (position: PositionType) =>
                            position.unit.id === Number(wing) &&
                            position.position.id === Number(designation) &&
                            !position.employee?.id,
                        ),
                        language,
                      )}
                      setValue={setValue}
                      placeholder={t(
                        "EMPLOYEE.GENERAL_INFO.FORM.POSITION.PLACEHOLDER",
                      )}
                      // disabled
                      disabled={isProfileDisabled}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.WING"
        control={control}
        name={GENERAL_INFO.WING_DESK_IO_E0}
        register={{ ...register(GENERAL_INFO.WING_DESK_IO_E0) }}
        options={getConvertedUnitData(wings, language)}
        setValue={setValue}
        disabled
        required
      />
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.DESIGNATION"
        control={control}
        name={GENERAL_INFO.DESIGNATION}
        register={{ ...register(GENERAL_INFO.DESIGNATION) }}
        options={getConvertedPositionData(
          positions
            .filter(
              (position: PositionType) => position.unit.id === Number(wing),
            )
            .map((position: PositionType) => position.position),
          language,
        )}
        setValue={setValue}
        disabled
        required
      />
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.GRADE"
        control={control}
        name={GENERAL_INFO.GRADE}
        register={{ ...register(GENERAL_INFO.GRADE) }}
        options={getFilteredGradeData(
          positions,
          wing,
          designation,
          language,
          "viewOnly",
        )}
        setValue={setValue}
        disabled
        required
      />
      <SelectControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.POSITION"
        control={control}
        name={GENERAL_INFO.POSITION}
        register={{ ...register(GENERAL_INFO.POSITION) }}
        options={getConvertedUnitPositionData(positions, language)}
        setValue={setValue}
        disabled
        required
      />
      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.RANK"
        register={{ ...register(GENERAL_INFO.RANK) }}
        error={errors}
        disabled={isProfileDisabled}
      />
      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.BASIC_SALARY"
        register={{ ...register(GENERAL_INFO.BASIC_SALARY) }}
        error={errors}
        type="number"
        disabled={isProfileDisabled}
      />
      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.POSTING_PLACE"
        register={{ ...register(GENERAL_INFO.POSTING_PLACE) }}
        error={errors}
        disabled={isProfileDisabled}
      />

      <InputTextRow
        label="EMPLOYEE.GENERAL_INFO.FORM.WORKING_PLACE"
        register={{ ...register(GENERAL_INFO.WORKING_PLACE) }}
        error={errors}
        disabled={isProfileDisabled}
      />
      <InputDateControllerRow
        label="EMPLOYEE.GENERAL_INFO.FORM.ORDER_DATE"
        control={control}
        register={{ ...register(GENERAL_INFO.ORDER_DATE) }}
        errors={errors}
        name={GENERAL_INFO.ORDER_DATE}
        disabled={isProfileDisabled}
      />
    </div>
  );
};

export default JobInfo;
